/**
 * @file        src/common/dcp-publish.js - Library code used by clients and tools (eg dcp-publish) to publish packages.
 *              Orginally part of dcpDeployPakage.
 *
 * @author     Ryan Rossiter, ryan@kingsds.network
 *             Greg Agnew, gagnew@sparc.network
 * @date       July 2020
 */

const protocol = require('dcp/protocol');
const { getId } = require('dcp/dcp-client/wallet');
const { requireNative } = require('dcp/dcp-client/webpack-native-bridge');
const debugging = require('dcp/debugging').scope('dcp-publish');
const { DCPError } = require('./dcp-error');
const { DcpURL } = require('dcp/common/dcp-url');

const log = (...args) => {
  if (debugging()) {
    console.debug('publish', ...args);
  }
};

/**
 * @typedef {object} PackageDescriptor
 * @property {string} name
 * @property {string} version
 * @property {Object.<string, string>} files Maps source file path to destination file name.
 *//**
 * @typedef {object} DeployPackagePayload
 * @property {string} name
 * @property {string} version
 * @property {Object.<string, string>} files Maps destination filename to file contents.
 */

/**
 * @param {PackageDescriptor} pkg
 * @param {string} baseDir Directory to use as the base when resolving paths to files.
 * @returns {DeployPackagePayload}
 */
function buildDeployPackagePayload(pkg, baseDir) {
  let { name, version, files } = pkg;
  const fs = requireNative('fs');
  const path = requireNative('path');

  const fileCount = Object.keys(files).length;
  if (fileCount === 0)
    throw new Error('Cannot deploy a package with no files');

  if (fileCount > 0) {
    // Sort files to help cache locality.
    const kvp = Object.entries(files);
    kvp.sort(([k1, v1], [k2, v2]) => {
      return v1 > v2 ? 1 : -1;
    })
    files = Object.fromEntries(kvp);
  }

  return {
    name, version,
    files: Object.entries(files).reduce((files, [sourcePath, destName]) => {
      const filepath = path.resolve(baseDir, sourcePath);
      files[destName] = fs.readFileSync(filepath, { encoding: 'utf-8' });
      return files;
    }, {}),
  };
}

/** @typedef {import('dcp/dcp-client/wallet/keystore').Keystore} Keystore */

/**
 * Publish a DCP package to the package manager identified by the current configuration.
 *
 * @param {PackageDescriptor} pkg An object describing the package or a filepath string.
 * @param {Keystore} [id] ID Keystore to deploy the package with, defaults to wallet.getId.
 * 
 * @throws when the package can't be located or is is rejected by the package manager. 
 */
exports.publish = async function publish(pkg, id) {
  const fs = requireNative('fs');
  const path = requireNative('path');

  let baseDir = '.';
  if (typeof pkg === 'string') {
    /* Set baseDir to resolve files listed in the package descriptor file relative to that file. */
    baseDir = path.dirname(pkg);
    pkg = JSON.parse(fs.readFileSync(pkg));
  }
  
  if (!id)
    id = await getId();

  const deployPackagePayload = buildDeployPackagePayload(pkg, baseDir);

  let packageManagerConnection;
  try
  {
    const packageManagerTarget = { location: new DcpURL(dcpConfig.packageManager.location) };
    packageManagerConnection = new protocol.Connection(packageManagerTarget, id);

    const { success, payload: deployPackageResponsePayload }
      = await packageManagerConnection.request('deployPackage', deployPackagePayload);
    log('deployPackage Response Payload:', deployPackageResponsePayload);

    if (!success)
      throw new DCPError(`Unable to publish DCP package '${pkg.name ?? 'unknown'}'`, deployPackageResponsePayload);

    return deployPackageResponsePayload;
  }
  finally
  {
    /* If instantiation of the connection fails, prevent throwing an obscure "close of undefined" error. */
    if (packageManagerConnection)
      packageManagerConnection.close();
  }
};
